import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Users,
  Calendar,
  Activity,
  Wallet,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  FileText,
  Phone,
  Home,
  Briefcase,
  Heart,
  Languages,
} from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const steps = [
  { n: "01", t: "Describe your issue", d: "Tell Justice-AI what happened in plain language — no legal jargon required." },
  { n: "02", t: "Get preliminary guidance", d: "Receive jurisdiction-aware advice grounded in verified legal materials." },
  { n: "03", t: "Match with a verified lawyer", d: "Our matching engine picks the lawyer best suited to your matter and location." },
  { n: "04", t: "Book a consultation", d: "Virtual, phone or in-person — schedule when it works for you." },
  { n: "05", t: "Track your case", d: "Live status, documents and payments on a secure dashboard." },
];

const categories = [
  { icon: Home, t: "Tenancy & Property" },
  { icon: Heart, t: "Family & Domestic" },
  { icon: ShieldCheck, t: "Police & Rights" },
  { icon: Briefcase, t: "Employment" },
  { icon: FileText, t: "Contracts & Consumer" },
  { icon: Users, t: "Human Rights" },
];

const HeroBlock = () => (
  <section className="relative pt-32 pb-24 overflow-hidden">
    <div className="absolute inset-0 opacity-60"><NeuralNetworkBackground density={24} /></div>
    <ScaleWatermark />
    <div className="turquoise-aura -top-32 -right-32 opacity-60" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
        <div className="aj-chip mb-6"><MessageSquare className="w-3 h-3" /> For Citizens</div>
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
          Free legal guidance.{" "}
          <span className="italic text-aj-gradient">Verified lawyers.</span>{" "}
          <span className="text-gold-gradient">Within minutes.</span>
        </h1>
        <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
          You don&apos;t need to know which lawyer to call. Tell Justice-AI your problem and we&apos;ll guide you,
          match you with the right lawyer, and stay with you until your matter is resolved.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/ai-assistant" data-testid="citizens-cta-help" className="btn-aj-primary">
            Talk to Justice-AI <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/register" data-testid="citizens-cta-register" className="btn-aj-secondary">
            Create Free Account
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const GetLegalHelp = () => (
  <div className="bg-ink text-white" data-testid="get-legal-help-page">
    <HeroBlock />

    {/* What you can ask about */}
    <Section eyebrow="What you can ask about" title={<>From <span className="italic text-aj-gradient">tenancy disputes</span> to <span className="text-gold-gradient">human rights</span>.</>}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {categories.map((c, i) => (
          <motion.div
            key={c.t}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
          >
            <GlassCard glow className="text-center">
              <c.icon className="w-7 h-7 text-turquoise mx-auto mb-3" />
              <div className="font-inter text-sm text-white/80">{c.t}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Steps */}
    <Section eyebrow="The Citizen Journey" title={<>From confusion to resolution — <span className="italic text-aj-gradient">in 5 steps.</span></>} aurora>
      <div className="space-y-4">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
            className="glass rounded-2xl p-6 lg:p-8 grid grid-cols-12 gap-6 items-center hover:border-turquoise/30 transition-all"
          >
            <div className="col-span-12 md:col-span-2">
              <div className="font-playfair italic text-5xl md:text-6xl text-gold-gradient leading-none">{s.n}</div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h3 className="font-playfair text-2xl text-white mb-2">{s.t}</h3>
              <p className="font-inter text-sm md:text-base text-white/60 leading-relaxed">{s.d}</p>
            </div>
            <div className="hidden md:flex col-span-1 justify-end">
              <CheckCircle2 className="w-6 h-6 text-turquoise/60" />
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Reassurance */}
    <Section eyebrow="Built for you" title={<>What this <span className="italic text-aj-gradient">does</span> and <span className="text-gold-gradient">does not</span> do.</>}>
      <div className="grid md:grid-cols-2 gap-6">
        <GlassCard glow>
          <h3 className="font-playfair text-2xl text-white mb-5">What Justice-AI does</h3>
          <ul className="space-y-3">
            {[
              "Analyzes your situation and identifies the legal issue",
              "Provides preliminary, jurisdiction-aware guidance",
              "Recommends evidence and documents you need",
              "Matches you with verified lawyers",
              "Tracks your case through every milestone",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-turquoise/15 border border-turquoise/40 flex items-center justify-center mt-0.5 shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-turquoise" />
                </div>
                <span className="font-inter text-sm text-white/75">{p}</span>
              </li>
            ))}
          </ul>
        </GlassCard>
        <GlassCard>
          <h3 className="font-playfair text-2xl text-white mb-5">What it doesn&apos;t replace</h3>
          <ul className="space-y-3">
            {[
              "A formal attorney-client relationship",
              "Legal advice from a verified lawyer",
              "Court representation or filings",
              "Personal due diligence on your matter",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-gold-400/15 border border-gold-400/40 flex items-center justify-center mt-0.5 shrink-0">
                  <span className="w-1.5 h-1.5 bg-gold-300 rounded-full" />
                </div>
                <span className="font-inter text-sm text-white/70">{p}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 p-4 rounded-xl bg-gold-400/[0.05] border border-gold-400/20">
            <p className="font-cormorant italic text-base text-gold-100 leading-relaxed">
              &ldquo;Justice-AI provides preliminary legal guidance, not a final legal opinion. A verified lawyer may be assigned to review your matter.&rdquo;
            </p>
          </div>
        </GlassCard>
      </div>
    </Section>

    {/* Final CTA */}
    <Section className="border-t border-white/8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow>Ready to start?</Eyebrow>
        <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-8">
          Your case begins with{" "}
          <span className="italic text-aj-gradient">a single question.</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/ai-assistant" data-testid="citizens-final-help" className="btn-aj-primary">
            Talk to Justice-AI
          </Link>
          <Link to="/contact" data-testid="citizens-final-contact" className="btn-aj-secondary">
            Speak to a human <Phone className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default GetLegalHelp;
